import React, { useEffect, useRef, useState } from "react";
import api, { API, getToken } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Smile, Frown, Mic, Square, Loader2, Heart, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { logger } from "@/lib/logger";

function todayKey() {
  return new Date().toISOString().slice(0, 10);
}

function useRecorder() {
  const [state, setState] = useState("idle");
  const [seconds, setSeconds] = useState(0);
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      rec.start();
      recRef.current = rec;
      setSeconds(0);
      setState("recording");
      timerRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
    } catch (e) {
      logger.error("Mic access denied", e);
      toast.error("Microphone permission needed");
    }
  };

  const stop = () =>
    new Promise((resolve) => {
      const rec = recRef.current;
      if (!rec) return resolve(null);
      setState("stopping");
      clearInterval(timerRef.current);
      rec.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
        rec.stream.getTracks().forEach((t) => t.stop());
        recRef.current = null;
        setState("idle");
        resolve(blob);
      };
      rec.stop();
    });

  return { state, seconds, start, stop };
}

export default function MoodCheckModal() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState("ask"); // ask | reason | affirm
  const [happy, setHappy] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [check, setCheck] = useState(null);
  const audioRef = useRef(null);
  const rec = useRecorder();

  useEffect(() => {
    if (!user || user === false) return;
    if (user.active === false) return;
    if (user.role === "super_admin") return;
    if (!user.terms_accepted) return; // Terms first
    if (!user.profile_completed) return; // KYC before mood
    // Don't show if we already checked today (cached in localStorage to skip API hop)
    const localKey = `hr_mood_done_${user.id}_${todayKey()}`;
    if (localStorage.getItem(localKey)) return;
    (async () => {
      try {
        const { data } = await api.get("/mood/today");
        if (data.exempt) return;
        if (data.has_checked) {
          localStorage.setItem(localKey, "1");
          return;
        }
        setStep("ask");
        setOpen(true);
      } catch (e) {
        logger.error("mood check fetch", e);
      }
    })();
  }, [user]);

  const transcribe = async () => {
    if (rec.state !== "recording") return;
    const blob = await rec.stop();
    if (!blob) return;
    setTranscribing(true);
    try {
      const form = new FormData();
      form.append("file", blob, "mood.webm");
      const token = getToken();
      const resp = await fetch(`${API}/mood/transcribe`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || "Transcription failed");
      }
      const { text } = await resp.json();
      setReason((c) => (c ? `${c}\n${text}` : text));
      toast.success("Got your voice — review the text before submitting");
    } catch (e) {
      toast.error(e.message || "Could not transcribe");
    } finally {
      setTranscribing(false);
    }
  };

  const submit = async () => {
    if (reason.trim().length < 3) {
      toast.error("Please share a little more about why");
      return;
    }
    setBusy(true);
    try {
      // Backend returns immediately now — affirmation text + TTS audio are
      // generated in the background. We move to the "affirm" step right away
      // and poll for the affirmation so the user is never stuck waiting.
      const { data } = await api.post("/mood/today", { happy, reason: reason.trim() });
      setCheck(data);
      setStep("affirm");
      if (!data.affirmation_text) {
        pollForAffirmation();
      } else if (data.affirmation_audio_b64 && audioRef.current) {
        audioRef.current.src = `data:audio/mp3;base64,${data.affirmation_audio_b64}`;
        try { await audioRef.current.play(); } catch (e) { logger.debug("autoplay blocked", e); }
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to submit");
    } finally {
      setBusy(false);
    }
  };

  // Poll GET /mood/today for the affirmation text + audio. Stops once both are
  // ready, the modal is closed, or 60s have elapsed (whichever first).
  const pollTimerRef = useRef(null);
  const pollDeadlineRef = useRef(0);
  const pollForAffirmation = () => {
    clearInterval(pollTimerRef.current);
    pollDeadlineRef.current = Date.now() + 60_000;
    pollTimerRef.current = setInterval(async () => {
      if (Date.now() > pollDeadlineRef.current) {
        clearInterval(pollTimerRef.current);
        return;
      }
      try {
        const { data } = await api.get("/mood/today");
        const c = data?.check;
        if (!c) return;
        setCheck((prev) => ({ ...(prev || {}), ...c }));
        if (c.affirmation_text && c.affirmation_audio_b64) {
          clearInterval(pollTimerRef.current);
        }
        if (c.affirmation_audio_b64 && audioRef.current && !audioRef.current.src) {
          audioRef.current.src = `data:audio/mp3;base64,${c.affirmation_audio_b64}`;
          try { await audioRef.current.play(); } catch (e) { logger.debug("autoplay blocked", e); }
        }
      } catch (e) {
        logger.debug("poll mood affirmation", e);
      }
    }, 1500);
  };

  useEffect(() => () => clearInterval(pollTimerRef.current), []);

  const close = () => {
    localStorage.setItem(`hr_mood_done_${user.id}_${todayKey()}`, "1");
    setOpen(false);
    // Reset for next time
    setTimeout(() => {
      setStep("ask");
      setHappy(null);
      setReason("");
      setCheck(null);
    }, 400);
  };

  if (!open) return null;

  // Three-step UI inside one un-dismissable modal
  const isHappy = happy === true;

  return (
    <Dialog open={open} onOpenChange={() => { /* required step — no escape */ }}>
      <DialogContent
        className="max-w-lg p-0 overflow-hidden border-0"
        data-testid="mood-modal"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        hideClose
      >
        {step === "ask" && (
          <div className="p-8 bg-gradient-to-b from-amber-50 to-white" data-testid="mood-step-ask">
            <div className="text-center space-y-2">
              <Heart className="w-7 h-7 mx-auto text-rose-500" strokeWidth={1.5} />
              <h2 className="font-display text-3xl font-black tracking-tighter">How are you feeling today, {user?.name?.split(" ")[0]}?</h2>
              <p className="text-sm text-zinc-600">Your honest answer helps us look after you better.</p>
            </div>
            <div className="grid grid-cols-2 gap-4 mt-8">
              <button
                onClick={() => { setHappy(true); setStep("reason"); }}
                className="group p-6 rounded-2xl border-2 border-emerald-200 bg-white hover:border-emerald-500 hover:bg-emerald-50 transition flex flex-col items-center gap-2"
                data-testid="mood-yes"
              >
                <Smile className="w-12 h-12 text-emerald-500 group-hover:scale-110 transition" strokeWidth={1.5} />
                <span className="font-bold text-emerald-700">Yes, I&apos;m happy</span>
              </button>
              <button
                onClick={() => { setHappy(false); setStep("reason"); }}
                className="group p-6 rounded-2xl border-2 border-orange-200 bg-white hover:border-orange-500 hover:bg-orange-50 transition flex flex-col items-center gap-2"
                data-testid="mood-no"
              >
                <Frown className="w-12 h-12 text-orange-500 group-hover:scale-110 transition" strokeWidth={1.5} />
                <span className="font-bold text-orange-700">Not really</span>
              </button>
            </div>
          </div>
        )}

        {step === "reason" && (
          <div className="p-8 space-y-4" data-testid="mood-step-reason">
            <div className="flex items-center gap-2">
              {isHappy ? (
                <Smile className="w-6 h-6 text-emerald-500" />
              ) : (
                <Frown className="w-6 h-6 text-orange-500" />
              )}
              <h2 className="font-display text-2xl font-black tracking-tight">
                {isHappy ? "What's making today good?" : "Tell us what's bothering you"}
              </h2>
            </div>
            <p className="text-sm text-zinc-500">
              {isHappy
                ? "A line or two — your positivity is contagious."
                : "We listen carefully. Anything you share stays between you and management."}
            </p>
            <Textarea
              rows={5}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={isHappy ? "e.g. Got good sleep, looking forward to today's shift…" : "e.g. Family is unwell, feeling tired, anything that's on your mind…"}
              data-testid="mood-reason"
            />
            <div className="flex items-center justify-between border-t border-zinc-200 pt-3">
              {(() => {
                if (rec.state === "recording") {
                  return (
                    <Button onClick={transcribe} variant="outline" className="border-red-300 text-red-700" data-testid="mood-stop-record">
                      <Square className="w-4 h-4 mr-2 fill-red-700" /> Stop &amp; transcribe ({rec.seconds}s)
                    </Button>
                  );
                }
                if (transcribing) {
                  return (
                    <Button disabled variant="outline">
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Transcribing…
                    </Button>
                  );
                }
                return (
                  <Button onClick={rec.start} variant="outline" data-testid="mood-record">
                    <Mic className="w-4 h-4 mr-2" /> Record voice
                  </Button>
                );
              })()}
              <Button
                onClick={submit}
                disabled={busy || reason.trim().length < 3}
                className={isHappy ? "bg-emerald-600 hover:bg-emerald-700" : "bg-orange-600 hover:bg-orange-700"}
                data-testid="mood-submit"
              >
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Share
              </Button>
            </div>
          </div>
        )}

        {step === "affirm" && check && (
          <div
            className={`p-8 space-y-4 ${check.happy ? "bg-gradient-to-b from-emerald-50 to-white" : "bg-gradient-to-b from-orange-50 to-white"}`}
            data-testid="mood-step-affirm"
          >
            <div className="text-center">
              <Sparkles className={`w-7 h-7 mx-auto ${check.happy ? "text-emerald-500" : "text-orange-500"}`} strokeWidth={1.5} />
              <h2 className="font-display text-2xl font-black tracking-tight mt-2">
                {check.happy ? "Thanks — we hear you!" : "We hear you"}
              </h2>
              <p className="text-xs text-zinc-500 mt-1">
                {check.affirmation_text
                  ? "A note from management"
                  : "Saved. Management is preparing a short note for you — feel free to continue."}
              </p>
            </div>
            <div className="bg-white border border-zinc-200 rounded-xl p-5 text-zinc-800 text-[15px] leading-relaxed whitespace-pre-wrap min-h-[88px]" data-testid="mood-affirmation-text">
              {check.affirmation_text || (
                <div className="flex items-center gap-2 text-zinc-400 text-sm">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Composing a personal note…
                </div>
              )}
            </div>
            {check.affirmation_audio_b64 && (
              <div className="flex items-center justify-center">
                <audio ref={audioRef} controls className="w-full max-w-md" data-testid="mood-affirmation-audio">
                  <track kind="captions" />
                </audio>
              </div>
            )}
            <div className="flex justify-end pt-2">
              <Button onClick={close} className="bg-zinc-950 hover:bg-zinc-800" data-testid="mood-continue">
                Continue to work
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
